<script>
import * as m from "@app/paraglide/messages.js";
import { toast } from "@zerodevx/svelte-toast";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    PRESET_TYPES,
    createPreset,
    updatePreset,
    removePreset,
    getPresetsByType,
} from "@app/store/presetStore.svelte.js";
import {
    BUILTIN_STANDARDS_PRESET_IDS,
    BUILTIN_STANDARDS_PRESETS,
    CUSTOM_STANDARDS_PRESET_ID,
    findMatchingStandardsPreset,
    isBuiltinStandardsPreset,
    normalizeStandardsConfig,
    standardsConfigsEqual,
} from "@app/lib/eventGroupStandards.js";

let {
    maxCps = $bindable(),
    maxWpm = $bindable(),
    maxChars = $bindable(),
    maxLines = $bindable(),
    minDuration = $bindable(),
    maxDuration = $bindable(),
    overlap = $bindable(),
    illegalChars = $bindable(),
    idPrefix = "eg-standards",
    heading = undefined,
} = $props();

let pinnedPresetId = $state(null);

const userPresets = $derived(getPresetsByType(PRESET_TYPES.EVENT_GROUP_STANDARDS));
const allPresets = $derived([...BUILTIN_STANDARDS_PRESETS, ...userPresets]);
const currentConfig = $derived({
    maxCps,
    maxWpm,
    maxChars,
    maxLines,
    minDuration,
    maxDuration,
    overlap,
    illegalChars,
});
const matchedByValues = $derived(
    findMatchingStandardsPreset(currentConfig, allPresets)?.id ?? CUSTOM_STANDARDS_PRESET_ID,
);
const displayedPresetId = $derived.by(() => {
    if (!pinnedPresetId) {
        return matchedByValues;
    }

    const pinned = allPresets.find((preset) => preset.id === pinnedPresetId);
    if (pinned && standardsConfigsEqual(currentConfig, pinned.config)) {
        return pinnedPresetId;
    }

    return matchedByValues;
});
const selectedPreset = $derived(
    allPresets.find((preset) => preset.id === displayedPresetId) ?? null,
);
const canDeleteSelected = $derived(
    Boolean(selectedPreset) && !isBuiltinStandardsPreset(selectedPreset),
);

function builtinPresetName(id) {
    if (id === BUILTIN_STANDARDS_PRESET_IDS.NA_BROADCAST) {
        return m.standards_preset_na_broadcast();
    }
    if (id === BUILTIN_STANDARDS_PRESET_IDS.EUROPEAN_TELETEXT) {
        return m.standards_preset_european_teletext();
    }
    return id;
}

function presetDisplayName(preset) {
    if (isBuiltinStandardsPreset(preset)) {
        return builtinPresetName(preset.id);
    }
    return preset.name;
}

function builtinDisplayNames() {
    return BUILTIN_STANDARDS_PRESETS.map((preset) => builtinPresetName(preset.id).toLowerCase());
}

function applyConfig(config) {
    const normalized = normalizeStandardsConfig(config);
    maxCps = normalized.maxCps;
    maxWpm = normalized.maxWpm;
    maxChars = normalized.maxChars;
    maxLines = normalized.maxLines;
    minDuration = normalized.minDuration;
    maxDuration = normalized.maxDuration;
    overlap = normalized.overlap;
    illegalChars = normalized.illegalChars;
}

function onPresetChange(event) {
    const id = event.currentTarget.value;
    if (id === CUSTOM_STANDARDS_PRESET_ID) {
        return;
    }

    const preset = allPresets.find((item) => item.id === id);
    if (!preset) {
        return;
    }

    applyConfig(preset.config);
    pinnedPresetId = id;
}

async function saveStandardsPreset() {
    const isUserSelected = canDeleteSelected;
    const defaultName = isUserSelected ? selectedPreset.name : "";

    const result = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.standards_preset_save_title(),
        text: m.standards_preset_save_prompt(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.standards_preset_save_confirm(),
        denyButtonText: m.standards_preset_cancel(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        inputLabel: m.standards_preset_name_label(),
        inputValue: defaultName || "",
        input: "text",
        customClass: SWAL_CLASSES.denyCancel,
        inputValidator: (value) => {
            const name = (value || "").trim();
            if (!name) {
                return m.standards_preset_name_required();
            }
            if (builtinDisplayNames().includes(name.toLowerCase())) {
                return m.standards_preset_builtin_name_conflict();
            }
            return undefined;
        },
    });

    if (!result.isConfirmed) {
        toast.push(m.standards_preset_save_aborted_toast(), { classes: ["toast-info"] });
        return;
    }

    const name = (result.value || "").trim();
    if (!name) {
        return;
    }

    const config = normalizeStandardsConfig(currentConfig);

    try {
        const existing = userPresets.find(
            (preset) => preset.name.toLowerCase() === name.toLowerCase(),
        );

        if (existing) {
            await updatePreset({
                ...existing,
                name,
                config,
            });
            pinnedPresetId = existing.id;
        } else {
            const created = await createPreset({
                name,
                type: PRESET_TYPES.EVENT_GROUP_STANDARDS,
                config,
            });
            pinnedPresetId = created.id;
        }

        toast.push(m.standards_preset_saved_toast({ name }), { classes: ["toast-success"] });
    } catch (error) {
        console.error("Error saving standards preset:", error);
        toast.push(m.standards_preset_save_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
    }
}

async function deleteStandardsPreset() {
    if (!canDeleteSelected) {
        return;
    }

    const presetName = selectedPreset.name;
    const presetId = selectedPreset.id;

    const result = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.standards_preset_delete_title(),
        text: m.standards_preset_delete_confirm_text({ name: presetName }),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.standards_preset_delete_confirm(),
        denyButtonText: m.standards_preset_cancel(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.destructiveDeny,
    });

    if (!result.isConfirmed) {
        return;
    }

    try {
        await removePreset(presetId);
        pinnedPresetId = null;
        toast.push(m.standards_preset_deleted_toast({ name: presetName }), {
            classes: ["toast-success"],
        });
    } catch (error) {
        console.error("Error deleting standards preset:", error);
        toast.push(m.standards_preset_delete_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
    }
}
</script>

<div>
    {#if heading}
        <hr />
        <p class="lead text-warning m-0">{heading}</p>
    {/if}
    <div class="row align-items-end mb-3 mt-2">
        <div class="col">
            <label class="form-label" for="{idPrefix}-standards-preset">
                {m.standards_preset_label()}
            </label>
            <select
                id="{idPrefix}-standards-preset"
                class="form-select"
                value={displayedPresetId}
                onchange={onPresetChange}
                aria-label={m.standards_preset_select_aria_label()}>
                <option
                    value={CUSTOM_STANDARDS_PRESET_ID}
                    disabled={displayedPresetId !== CUSTOM_STANDARDS_PRESET_ID}>
                    {m.standards_preset_custom()}
                </option>
                {#each allPresets as preset (preset.id)}
                    <option value={preset.id}>{presetDisplayName(preset)}</option>
                {/each}
            </select>
        </div>
        <div class="col-auto">
            <div
                class="btn-group"
                role="group"
                aria-label={m.standards_preset_actions_aria_label()}>
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    title={m.standards_preset_save_button_title()}
                    aria-label={m.standards_preset_save_button_aria_label()}
                    onclick={saveStandardsPreset}>
                    <i class="bi bi-save" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    title={m.standards_preset_delete_button_title()}
                    aria-label={m.standards_preset_delete_button_aria_label()}
                    disabled={!canDeleteSelected}
                    onclick={deleteStandardsPreset}>
                    <i class="bi bi-trash" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </div>
    <div class="row">
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-maxCps">
                {m.max_characters_per_second()}
            </label>
            <input
                id="{idPrefix}-maxCps"
                type="number"
                step="1"
                min="0"
                max="9999"
                class="form-control"
                bind:value={maxCps} />
        </div>
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-maxWpm">
                {m.max_words_per_minute()}
            </label>
            <input
                id="{idPrefix}-maxWpm"
                type="number"
                step="1"
                min="0"
                max="9999"
                class="form-control"
                bind:value={maxWpm} />
        </div>
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-maxChars">
                {m.max_characters_per_line()}
            </label>
            <input
                id="{idPrefix}-maxChars"
                type="number"
                step="1"
                min="0"
                max="9999"
                class="form-control"
                bind:value={maxChars} />
        </div>
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-maxLines">
                {m.max_lines_per_event()}
            </label>
            <input
                id="{idPrefix}-maxLines"
                type="number"
                step="1"
                min="0"
                max="9999"
                class="form-control"
                bind:value={maxLines} />
        </div>
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-minDuration">
                {m.min_event_duration()}
            </label>
            <input
                id="{idPrefix}-minDuration"
                type="number"
                step="0.01"
                min="0"
                max="9999"
                class="form-control"
                bind:value={minDuration} />
        </div>
        <div class="col-6 mb-3">
            <label class="form-label" for="{idPrefix}-maxDuration">
                {m.max_event_duration()}
            </label>
            <input
                id="{idPrefix}-maxDuration"
                type="number"
                step="0.01"
                min="0"
                max="9999"
                class="form-control"
                bind:value={maxDuration} />
        </div>
        <div class="col-6 mb-3">
            <div class="form-check form-switch">
                <input
                    type="checkbox"
                    class="form-check-input"
                    id="{idPrefix}-overlap"
                    bind:checked={overlap} />
                <label class="form-check-label" for="{idPrefix}-overlap">
                    {m.overlap_detection()}
                </label>
            </div>
        </div>
        <div class="col-6 mb-3">
            <div class="form-check form-switch">
                <input
                    type="checkbox"
                    class="form-check-input"
                    id="{idPrefix}-illegalChars"
                    bind:checked={illegalChars} />
                <label class="form-check-label" for="{idPrefix}-illegalChars">
                    {m.detect_illegal_characters_608()}
                </label>
            </div>
        </div>
    </div>
</div>
